// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Button, Form } from "react-bootstrap";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { COMPANY_EMBYT_ID } from "shared/types/cloudtypes";
import { ICondition, IParameter } from "shared/types/parameter";
import { IState } from "states/states";
import { ActionType, IAction, raiseAction } from "actions";
import { isDirectConnection } from "guiutils";
import { isSet } from "shared/utils/utils";
import { ConditionViewMode } from "states/guistates";

import ConditionGraph from "view/comp/ConditionGraph";
import SiteBar from "view/comp/SiteBar";

// how often the live block status is re-read while this page is open; matches the direct
// connection poll rate used elsewhere (DATA_POLL_REOCCURANCE_LINX in sagas.ts)
const BLOCK_STATUS_POLL_INTERVAL = 2000;

interface IConditionDetailPageProps {
  condition: ICondition | undefined;
  conditionIndex: number | undefined;
  parameter: IParameter | undefined;
  hasLiveView: boolean;
  viewMode: ConditionViewMode;
  blockResult: number[] | undefined;
  dataValue: number[] | undefined;
  connectionStatus: number[] | undefined;
}

const mapStateToProps = (state: IState) => {
  const isDirect = isDirectConnection(state);
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);

  // the block status in state.data may be a leftover of a previously viewed condition
  const blockStatus =
    isDirect && state.data.conditionBlockStatus?.conditionIndex === state.router.condition ?
      state.data.conditionBlockStatus
    : undefined;

  const props: IConditionDetailPageProps = {
    condition:
      state.router.condition !== undefined ?
        state.para.parameter?.condition[state.router.condition]
      : undefined,
    conditionIndex: state.router.condition,
    parameter: state.para.parameter,
    hasLiveView: isDirect && curUser?.companyId === COMPANY_EMBYT_ID,
    // kept in gui state so the chosen view survives navigating to a different condition
    viewMode: state.gui.conditionViewMode,
    blockResult: blockStatus?.blockResult,
    dataValue: blockStatus?.dataValue,
    connectionStatus: blockStatus?.connectionStatus,
  };
  return props;
};

interface IConditionDetailPageDispatch {
  getConditionBlockStatus: (conditionIndex: number) => void;
  onSelectViewMode: (viewMode: ConditionViewMode) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IConditionDetailPageDispatch = {
    getConditionBlockStatus: (conditionIndex: number) =>
      dispatch(raiseAction(ActionType.ACT_GET_CONDITIONBLOCKSTATUS_REQ, conditionIndex)),
    onSelectViewMode: (viewMode: ConditionViewMode) =>
      dispatch(raiseAction(ActionType.ACT_SELECT_CONDITIONVIEWMODE, viewMode)),
  };
  return dispatchProps;
};

class ConditionDetailPageComponent extends React.Component<
  IConditionDetailPageProps & IConditionDetailPageDispatch & RoutedProps
> {
  private pollInterval: ReturnType<typeof setInterval> | undefined;

  public componentDidMount() {
    this.updatePolling();
  }

  public componentDidUpdate(prevProps: IConditionDetailPageProps) {
    if (
      prevProps.conditionIndex !== this.props.conditionIndex ||
      prevProps.viewMode !== this.props.viewMode ||
      prevProps.hasLiveView !== this.props.hasLiveView
    ) {
      this.updatePolling();
    }
  }

  public componentWillUnmount() {
    clearInterval(this.pollInterval);
  }

  // the live block status is only needed (and only fetched) while the live view is actually
  // shown, i.e. selected and available at all (direct connection, embyt user)
  private updatePolling() {
    clearInterval(this.pollInterval);
    this.pollInterval = undefined;
    if (!this.props.hasLiveView || this.props.viewMode !== ConditionViewMode.LIVE) {
      return;
    }
    this.requestBlockStatus();
    this.pollInterval = setInterval(() => this.requestBlockStatus(), BLOCK_STATUS_POLL_INTERVAL);
  }

  private requestBlockStatus() {
    if (this.props.hasLiveView && isSet(this.props.conditionIndex)) {
      this.props.getConditionBlockStatus(this.props.conditionIndex);
    }
  }

  public render() {
    const showLive = this.props.hasLiveView && this.props.viewMode === ConditionViewMode.LIVE;
    return (
      <>
        <SiteBar showPeriodSelect={false} />
        <div id="conditiondetail" className="flex-self flex-content">
          <div className="headerbuttons mb-2">
            <div className="left">
              <div className="buttonbar d-print-none">
                <Button
                  id="navigate-back"
                  variant="secondary"
                  onClick={() => this.props.navigate(-1)}
                >
                  <FormattedMessage id="Back to condition list" />
                </Button>
              </div>
            </div>
            {this.props.hasLiveView && (
              <div className="d-print-none">
                <Form.Check
                  inline
                  id="conditionview-parameter"
                  name="conditionview"
                  type="radio"
                  label={<FormattedMessage id="Parameter view" />}
                  checked={this.props.viewMode === ConditionViewMode.PARAMETER}
                  onChange={() => this.props.onSelectViewMode(ConditionViewMode.PARAMETER)}
                />
                <Form.Check
                  inline
                  id="conditionview-live"
                  name="conditionview"
                  type="radio"
                  label={<FormattedMessage id="Live view" />}
                  checked={this.props.viewMode === ConditionViewMode.LIVE}
                  onChange={() => this.props.onSelectViewMode(ConditionViewMode.LIVE)}
                />
              </div>
            )}
          </div>
          <h1>{this.props.condition?.name}</h1>
          {this.props.condition ?
            <ConditionGraph
              condition={this.props.condition}
              parameter={this.props.parameter}
              showLive={showLive}
              blockResult={this.props.blockResult}
              dataValue={this.props.dataValue}
              connectionStatus={this.props.connectionStatus}
            />
          : <FormattedMessage id="Condition not found." />}
        </div>
      </>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(ConditionDetailPageComponent));
